import App from 'app/app.js';
import 'app/service/RouterService.js';
import 'app/service/ErrorLogService.js';
import 'app/service/StatsdService.js';
import 'app/store/UserProfileStore.js';
import 'app/store/MessageStore.js';

function IdentityService(
  $http,
  $q,
  $window,
  $injector,
  $location,
  ErrorLogService,
  MessageStore,
  StatsdService,
  TranslateProviderService
) {
  var logoutAction = function () {
    try {
      var UserProfileStore = $injector.get('UserProfileStore');
      if (UserProfileStore.isUserLoggedInWithIdentity()) {
        $injector.get('RouterService').goToHome();
        UserProfileStore.logout();
      }
    } catch (e) {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      e.message = 'Problem with IdentityService.logoutAction: ' + e.message;
      ErrorLogService(e);
    }
  };

  var getLang = function () {
    return TranslateProviderService.getLocale().split('-')[0];
  };

  const getBaseEcidObj = function () {
    try {
      const orgId = $window._satellite?.company?.orgId;
      if (!$window.Visitor || !orgId) return {};
      const ecidQ = $window.Visitor.getInstance(orgId)?.appendVisitorIDsTo('')?.replace('?adobe_mc=', '');
      return ecidQ ? { adobe_mc: ecidQ } : {};
    } catch (e) {
      return {};
    }
  };

  this.initIdentity = function (token, error) {
    var UserProfileStore = $injector.get('UserProfileStore');
    if (token) {
      $window.document.addEventListener('sflySessionLogout', logoutAction);
      return UserProfileStore.loadIdentityUser();
    }
    // '000' error = no user logged into identity
    if (!!error && !!error.error && error.error.code !== '000') {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      ErrorLogService(new Error('Identity failed to getIdToken. Code: ' + error.error.code));
    } else if (UserProfileStore.isUserLoggedInWithIdentity()) {
      return UserProfileStore.logout();
    } else {
      return UserProfileStore.loadLegacyUser();
    }
  };

  this.resyncIdentity = function () {
    var UserProfileStore = $injector.get('UserProfileStore');

    return $window.$sflyAuth
      .resyncTokens()
      .then(
        function () {
          return UserProfileStore.loadIdentityUser();
        }.bind(this)
      )
      .catch(function (error) {
        // '000' error = no user logged into identity
        if (!!error && !!error.error && error.error.code !== '000') {
          MessageStore.createSystem('COMMON_TEXT_ERROR');
          StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
          ErrorLogService(new Error('Identity failed to resyncTokens. Code: ' + error.error.code));
        }
      });
  };

  this.getCurrentUser = function () {
    try {
      return $window.$sflyAuth && $window.$sflyAuth.currentUser;
    } catch (e) {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      e.message = 'Problem with IdentityService.getCurrentUser: ' + e.message;
      ErrorLogService(e);
    }
  };

  this.getIdentityToken = function () {
    try {
      return $window.$sflyAuth && $window.$sflyAuth.refreshToken;
    } catch (e) {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      e.message = 'Problem with IdentityService.getIdentityToken: ' + e.message;
      ErrorLogService(e);
    }
  };

  this.getJwt = function () {
    return $window.$sflyAuth
      .getIdToken()
      .then(function (data) {
        return data.jwtToken;
      })
      .catch(function (response) {
        if (!!response && !!response.error && response.error.code !== '000') {
          MessageStore.createSystem('COMMON_TEXT_ERROR');
          StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
          var exception = new Error('Problem with IdentityService.getJwt. Code: ' + response.error.code);
          ErrorLogService(exception);
        }
      });
  };

  this.isElevationRequired = function () {
    try {
      return $window.$sflyAuth && $window.$sflyAuth.isElevationNeeded();
    } catch (e) {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      e.message = 'Problem with IdentityService.isElevationRequired: ' + e.message;
      ErrorLogService(e);
    }
  };

  this.loginIdentity = function (email, redirectUrl, changeAccountUrl) {
    try {
      const payload = {
        brand: 'mlt',
        email: encodeURIComponent(email),
        redirectUri: encodeURI(redirectUrl),
        changeAccountUri: encodeURI(changeAccountUrl),
        lang: getLang(),
        ...getBaseEcidObj()
      };

      const userType = $injector.get('UserProfileStore').getUserType();
      const isNewOrGuestUser = userType === 'NEW' || userType === 'GUEST';
      if (!isNewOrGuestUser && $window.migrateBrandAccountSignInEnabled === true) {
        $window.$sflyAuth.migrateBrandAccountSignIn(payload);
      } else {
        $window.$sflyAuth.migrateBrandAccount(payload);
      }
    } catch (e) {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      e.message = 'Problem with IdentityService.loginIdentity: ' + e.message;
      ErrorLogService(e);
    }
  };

  this.goToElevation = function (redirectUrl) {
    if (!redirectUrl) {
      redirectUrl = $location.absUrl().match(/^([^#]*)/)[1]; // base url
    }
    try {
      $window.$sflyAuth.requireElevation({
        brand: 'mlt',
        redirectUri: encodeURI(redirectUrl),
        lang: getLang(),
        ...getBaseEcidObj()
      });
    } catch (e) {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      e.message = 'Problem with IdentityService.goToElevation: ' + e.message;
      ErrorLogService(e);
    }
  };

  this.isEmailIdentityEligible = function (email) {
    var deferred = $q.defer();
    $http
      .post('./identity/isUserEligibleForIdentity', { email: email })
      .then(function (response) {
        if (response.data.success) {
          deferred.resolve(response.data.data.identityEligible);
        } else {
          deferred.reject();
        }
      })
      .catch(function () {
        deferred.reject();
        StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      });

    return deferred.promise;
  };

  this.logout = function () {
    var url = $injector.get('RouterService').getSPAPath() + '#/';
    try {
      $window.$sflyAuth.signout({
        redirectUri: encodeURI(url),
        ...getBaseEcidObj()
      });
    } catch (e) {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      e.message = 'Problem with IdentityService.logout: ' + e.message;
      ErrorLogService(e);
    }
  };

  this.identityChangePassword = function () {
    const RouterService = $injector.get('RouterService');
    const redirectPath = RouterService.paths.MY_ACCOUNT || '/my-account/';
    const url = RouterService.getSPAPath() + '#' + redirectPath;
    try {
      $window.$sflyAuth.changePassword({
        brand: 'mlt',
        redirectUri: encodeURI(url),
        lang: getLang(),
        ...getBaseEcidObj()
      });
    } catch (e) {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      e.message = 'Problem with IdentityService.identityChangePassword: ' + e.message;
      ErrorLogService(e);
    }
  };

  this.identityShowChangeProfile = function () {
    const RouterService = $injector.get('RouterService');
    const redirectPath = RouterService.paths.MY_ACCOUNT || '/my-account/';
    const url = RouterService.getSPAPath() + '#' + redirectPath;
    try {
      $window.$sflyAuth.showChangeProfile({
        brand: 'mlt',
        redirectUri: encodeURI(url),
        lang: getLang(),
        ...getBaseEcidObj()
      });
    } catch (e) {
      MessageStore.createSystem('COMMON_TEXT_ERROR');
      StatsdService.incrementCounter('IDENTITY_SVC_FAILURE');
      e.message = 'Problem with IdentityService.identityShowChangeProfile: ' + e.message;
      ErrorLogService(e);
    }
  };
}

App.service('IdentityService', [
  '$http',
  '$q',
  '$window',
  '$injector',
  '$location',
  'ErrorLogService',
  'MessageStore',
  'StatsdService',
  'TranslateProviderService',
  IdentityService
]);
